import {IconArrowUp} from "@tabler/icons-react";
import {memo, useCallback, useEffect, useRef} from "react";
import styles from "./ScrollToTop.module.css";

const SHOW_THRESHOLD = 400;
const CIRCUMFERENCE = 2 * Math.PI * 20;

const ScrollToTop = () => {
  const buttonRef = useRef(null);
  const progressFillRef = useRef(null);
  const rafRef = useRef(0);

  useEffect(() => {
    let lastVisible = false;
    let lastProgressKey = -1;

    const update = () => {
      const scrollY = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const nowVisible = scrollY > SHOW_THRESHOLD;
      const nowProgress = nowVisible && docHeight > 0 ? Math.min(Math.round((scrollY / docHeight) * 100) / 100, 1) : 0;

      if (nowVisible !== lastVisible) {
        lastVisible = nowVisible;
        const btn = buttonRef.current;
        if (btn) {
          btn.classList.toggle(styles.visible, nowVisible);
        }
        if (!nowVisible) {
          lastProgressKey = -1;
          const fill = progressFillRef.current;
          if (fill) {
            fill.setAttribute("stroke-dashoffset", String(CIRCUMFERENCE));
          }
          return;
        }
      }

      if (nowVisible && nowProgress !== lastProgressKey) {
        lastProgressKey = nowProgress;
        const fill = progressFillRef.current;
        if (fill) {
          const dashOffset = CIRCUMFERENCE * (1 - nowProgress);
          fill.setAttribute("stroke-dashoffset", String(dashOffset));
        }
      }
    };

    const onScroll = () => {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(update);
    };

    window.addEventListener("scroll", onScroll, {passive: true});
    update();
    return () => {
      window.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const scrollToTop = useCallback(() => {
    window.scrollTo({top: 0, behavior: "smooth"});
  }, []);

  return (
    <button
      ref={buttonRef}
      type="button"
      className={styles.button}
      onClick={scrollToTop}
      aria-label="Scroll to top"
    >
      <svg
        className={styles.progressRing}
        viewBox="0 0 46 46"
        aria-hidden="true"
        role="presentation"
      >
        <circle
          className={styles.progressTrack}
          cx="23"
          cy="23"
          r="20"
        />
        <circle
          ref={progressFillRef}
          className={styles.progressFill}
          cx="23"
          cy="23"
          r="20"
          strokeDasharray={CIRCUMFERENCE}
          strokeDashoffset={CIRCUMFERENCE}
        />
      </svg>
      <IconArrowUp size={18} />
    </button>
  );
};

export default memo(ScrollToTop);
